import { FC, useCallback, useMemo, useRef, useState } from 'react';
import styles from './StanTipTapEditorToolbar.module.css';
import { cn } from '@/lib/utils/cn';
import {
    BareAiGenerateIcon,
    BareBoldIcon,
    BareHeadingIcon,
    BareItalicIcon,
    BareLinkIcon,
    BareListIcon,
    BareStrikeIcon,
} from '../icons';
import { FiImage, FiPlus } from 'react-icons/fi';
import { GoVideo } from 'react-icons/go';

import { StanButton, StanDropdownMenu, StanFloatingMenu, StanTextArea } from '@/components';
import { StanTipTapEditorToolbarProps, ToolbarButton } from '../types';
import { StanFileUpload, StanFileUploadRef } from '../../StanFileUpload/StanFileUpload';
import { useModal } from '@/hooks/useModal';
import { LinkUploadModal } from '../Modals/LinkUploadModal';
import { VideoUploadModal } from '../Modals/VideoUploadModal';
import { ProductSelectionModal } from '../Modals/ProductSelectionModal';
import { configSettings, stanAnalytics, stanNotify } from '@/lib';
import { useUser } from '@/queries/useUser';
import { DropdownProvider } from '../../StanDropdown/DropdownProvider';
import { EditorActions } from '../editor.logic';
import { useEditorState } from '../hooks/useEditorState';

// TODO : Countdown Timer

export const StanTipTapEditorToolbar: FC<StanTipTapEditorToolbarProps> = ({
    editor,
    className,
    disabled,
    error = false,
    imageUploadAllowed = false,
    linkAllowed = false,
    videoAllowed = false,
    generateAiAllowed = false,
    personalizeItemData = [],
    allowAddProductLink = true,
    onAiGenerate,
    isAiGenerating = false,
    visibleButtons,
}) => {
    const { user } = useUser();
    const [generateAiPrompt, setGenerateAiPrompt] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileUploadRef = useRef<StanFileUploadRef>(null);
    const fileUploadVideoRef = useRef<StanFileUploadRef>(null);
    const { isOpen: isOpenLinkUploadModal, open: openLinkUploadModal, close: closeLinkUploadModal } = useModal();
    const { isOpen: isOpenVideoUploadModal, open: openVideoUploadModal, close: closeVideoUploadModal } = useModal();
    const {
        isOpen: isOpenProductSelectionModal,
        open: openProductSelectionModal,
        close: closeProductSelectionModal,
    } = useModal();
    const actions = useMemo(() => new EditorActions(editor), [editor]);
    const editorState = useEditorState(editor);

    const errorUploadingVideo = (error: Error) => {
        stanAnalytics('video-upload-error', {
            meta: {
                user_id: user?.user_id,
                username: user?.username,
                location: 'tip-tap-editor',
            },
            props: {
                error: error.message,
            },
        });
    };

    const requestAIAssistance = useCallback(
        async (prompt: string) => {
            if (!onAiGenerate || !prompt.trim() || !editor) return;
            try {
                await onAiGenerate(prompt);
            } catch {
                stanNotify({
                    type: 'error',
                    title: 'Failed to reach Stanley.',
                    text: 'Please try again',
                    duration: 5000,
                });
            }
        },
        [editor, onAiGenerate]
    );

    const secondaryButtons = useMemo(() => {
        const buttons: ToolbarButton[] = [];

        if (personalizeItemData.length > 0) {
            buttons.push({
                label: 'Personalize',
                onClick: () => {},
                id: 'personalize',
                icon: FiPlus,
                type: 'dropdown',
                items: personalizeItemData.map(item => ({
                    label: item.name,
                    value: item.id,
                })),
            });
        }

        if (allowAddProductLink) {
            buttons.push({
                label: 'Add Product Link',
                onClick: openProductSelectionModal,
                id: 'add-product-link',
                icon: BareLinkIcon,
            });
        }

        return buttons;
    }, [personalizeItemData, allowAddProductLink, openProductSelectionModal]);

    if (!editor) return null;

    // Helper function to check if a button should be visible
    const isButtonVisible = (buttonType: string) => {
        if (!visibleButtons) return true;
        return visibleButtons.includes(buttonType as any);
    };

    return (
        <div className={cn(styles.toolbarContainer, disabled && styles.disabled, className, error && styles.error)}>
            <div className={cn(styles.mainToolbar, className, disabled && styles.disabled)}>
                {isButtonVisible('heading') && (
                    <button
                        type='button'
                        onClick={() => actions.toggleHeading()}
                        className={cn(styles.toolbarButton, editorState.isHeading && styles.active)}
                    >
                        {BareHeadingIcon()}
                    </button>
                )}
                {isButtonVisible('bold') && (
                    <button
                        type='button'
                        onClick={() => actions.toggleBold()}
                        className={cn(styles.toolbarButton, editorState.isBold && styles.active)}
                    >
                        {BareBoldIcon()}
                    </button>
                )}
                {isButtonVisible('strike') && (
                    <button
                        type='button'
                        onClick={() => actions.toggleStrike()}
                        className={cn(styles.toolbarButton, editorState.isStrike && styles.active)}
                    >
                        {BareStrikeIcon()}
                    </button>
                )}
                {isButtonVisible('italic') && (
                    <button
                        type='button'
                        onClick={() => actions.toggleItalic()}
                        className={cn(styles.toolbarButton, editorState.isItalic && styles.active)}
                    >
                        {BareItalicIcon()}
                    </button>
                )}
                {isButtonVisible('list') && (
                    <button
                        type='button'
                        onClick={() => actions.toggleBulletList()}
                        className={cn(styles.toolbarButton, editorState.isBulletList && styles.active)}
                    >
                        {BareListIcon()}
                    </button>
                )}
                {imageUploadAllowed && (
                    <button
                        type='button'
                        onClick={() => fileUploadRef.current?.onPickFile()}
                        className={cn(styles.toolbarButton)}
                    >
                        <FiImage className={styles.toolbarButtonIcon} style={{ width: '20px', height: '20px' }} />
                    </button>
                )}
                {linkAllowed && (
                    <button type='button' onClick={() => openLinkUploadModal()} className={cn(styles.toolbarButton)}>
                        {BareLinkIcon()}
                    </button>
                )}
                {videoAllowed && (
                    <button type='button' onClick={() => openVideoUploadModal()} className={cn(styles.toolbarButton)}>
                        <GoVideo className={styles.toolbarButtonIcon} style={{ width: '20px', height: '20px' }} />
                    </button>
                )}
            </div>
            <div className={cn(styles.secondaryToolbar, className)}>
                {generateAiAllowed && (
                    <StanFloatingMenu
                        id='ai-generate-menu'
                        name='ai-generate-menu'
                        trigger={
                            <button
                                type='button'
                                className={cn(
                                    styles.toolbarButton,
                                    styles.aiGenerateButton,
                                    error && styles.error,
                                    (disabled && styles.disabled) || (isAiGenerating && styles.disabled)
                                )}
                            >
                                {BareAiGenerateIcon()}
                                <span
                                    className={cn(
                                        styles.aiGenerateText,
                                        error && styles.error,
                                        (disabled && styles.disabled) || (isAiGenerating && styles.disabled)
                                    )}
                                >
                                    Generate with AI
                                </span>
                            </button>
                        }
                        placement='bottom-start'
                        offset={10}
                        minWidth={320}
                        className={styles.popover}
                        onOpenChange={isOpen => {
                            if (isOpen) {
                                setTimeout(() => {
                                    textareaRef.current?.focus();
                                }, 100);
                            }
                        }}
                    >
                        {({ closeMenu }) => (
                            <>
                                <h6 className={styles.popoverTitle}>Generate with AI</h6>
                                <div className={styles.popoverContent}>
                                    <StanTextArea
                                        ref={textareaRef}
                                        id='tiptap-ai-prompt'
                                        name='tiptap-ai-prompt'
                                        placeholder='Briefly describe your product and what makes it special to your audience...'
                                        value={generateAiPrompt}
                                        onChange={e => setGenerateAiPrompt(e.target.value)}
                                    />
                                    <span className={styles.popoverLetterCount}>{generateAiPrompt.length}/800</span>
                                </div>

                                <div className={styles.popoverActions}>
                                    <StanButton
                                        name='ai-generate-button'
                                        size='sm'
                                        disabled={!generateAiPrompt.trim()}
                                        onClick={() => {
                                            requestAIAssistance(generateAiPrompt);
                                            setGenerateAiPrompt('');
                                            closeMenu();
                                        }}
                                    >
                                        {BareAiGenerateIcon()}
                                        Generate
                                    </StanButton>
                                </div>
                            </>
                        )}
                    </StanFloatingMenu>
                )}
                {secondaryButtons?.map(button =>
                    button.type === 'dropdown' ? (
                        <DropdownProvider key={button.id} dropdownId={button.id} dropdownName={button.id}>
                            <StanDropdownMenu
                                id={button.id}
                                name={button.id}
                                options={button.items || []}
                                onSelect={(_, opt) => {
                                    actions.insertVariableItem(opt.value, opt.label);
                                }}
                            >
                                <StanButton
                                    key={button.id}
                                    name={button.id}
                                    size='xs'
                                    variant='outline'
                                    onClick={button.onClick}
                                    color={error ? 'destructive' : 'primary'}
                                    disabled={disabled}
                                    icon={button.icon}
                                >
                                    {button.label}
                                </StanButton>
                            </StanDropdownMenu>
                        </DropdownProvider>
                    ) : (
                        <StanButton
                            key={button.id}
                            name={button.id}
                            size='xs'
                            variant='outline'
                            onClick={button.onClick}
                            color={error ? 'destructive' : 'primary'}
                            disabled={disabled}
                            icon={button.icon}
                        >
                            {button.label}
                        </StanButton>
                    )
                )}
            </div>

            <StanFileUpload
                ref={fileUploadRef}
                objectType='image'
                onUploaded={payload => actions.insertImage(payload.src)}
                accept='image/bmp,image/jpeg,image/png,image/gif,img/svg+xml'
            />
            <StanFileUpload
                ref={fileUploadVideoRef}
                accept='video/*'
                onUploaded={payload =>
                    actions.insertIframe(`https://${configSettings.noHttpsFrontDomain}/embed?src=${payload.src}`)
                }
                onError={errorUploadingVideo}
            />
            <LinkUploadModal isOpen={isOpenLinkUploadModal} onClose={closeLinkUploadModal} editor={editor} />
            <VideoUploadModal
                isOpen={isOpenVideoUploadModal}
                onClose={closeVideoUploadModal}
                editor={editor}
                onUploadVideo={() => fileUploadVideoRef.current?.onPickFile()}
            />
            <ProductSelectionModal
                isOpen={isOpenProductSelectionModal}
                onClose={closeProductSelectionModal}
                editor={editor}
            />
        </div>
    );
};
